/** Inbox surface — flat triage list on Battlefield (Phase 6). */
import { useState, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useTasksContext, TaskUIProvider } from '@/features/tasks';
import {
  useBreakpoint,
  isBattlefieldBreakpoint,
} from '@/hooks/ui/useBreakpoint';
import TaskList from '@/features/tasks/components/lists/TaskList';
import BulkActionBar from '@/features/today/components/BulkActionBar';
import { getInboxTasks } from '@/features/organize/utils/inboxTriageUtils';
import { useInboxViewUiContext } from '@/features/organize/hooks/use-inbox-view-ui-context';
import { useT } from '@/i18n/useT';

const MOBILE_PAGE_PADDING = 1.5;

const InboxPage = () => {
  const t = useT();
  const { filteredTasks, user } = useTasksContext();
  const currentBreakpoint = useBreakpoint();
  const isBattlefield = isBattlefieldBreakpoint(currentBreakpoint);
  const inboxTasks = useMemo(
    () => getInboxTasks(filteredTasks),
    [filteredTasks],
  );

  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const isMultiSelectActive = selectedTaskIds.length > 1;

  const onToggleInSelection = useCallback((taskId) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId],
    );
  }, []);

  const onClearSelection = useCallback(() => {
    setSelectedTaskIds([]);
  }, []);

  const uiContextValue = useInboxViewUiContext({
    selectedTaskIds,
    isMultiSelectActive,
    onToggleInSelection,
  });

  // Desktop: Inbox is Organize-tab only. Battlefield keeps a compact flat list.
  if (!isBattlefield) {
    return <Navigate to="/app/organize?tab=inbox" replace />;
  }

  return (
    <TaskUIProvider value={uiContextValue}>
      <Box
        data-testid="inbox-page"
        sx={{
          p: MOBILE_PAGE_PADDING,
          width: '100%',
          minHeight: '40vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {inboxTasks.length === 0 ? (
          <Typography
            data-testid="inbox-empty-state"
            variant="body2"
            color="text.secondary"
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              py: 4,
              px: 2,
            }}
          >
            {t('inboxpage.empty', 'No tasks waiting for triage.')}
          </Typography>
        ) : (
          <TaskList
            tasks={inboxTasks}
            selectedTaskIds={selectedTaskIds}
            onSelectionChange={setSelectedTaskIds}
            view="today"
            indentationType="none"
            showBreadcrumbs
            id="inbox-flat-list"
          />
        )}
        {isMultiSelectActive ? (
          <BulkActionBar
            selectedTaskIds={selectedTaskIds}
            onClearSelection={onClearSelection}
            user={user}
          />
        ) : null}
      </Box>
    </TaskUIProvider>
  );
};

export default InboxPage;
